import React, {createContext, useCallback, useContext, useRef, useState} from 'react';
import Toast, {ToastContainer} from 'components/ui/Toast';

const ToastContext = createContext(null);

let nextId = 0;

export const ToastProvider = ({children}) => {
	const [toasts, setToasts] = useState([]);
	const timers = useRef({});

	const dismiss = useCallback(id => {
		clearTimeout(timers.current[id]);
		delete timers.current[id];
		setToasts(prev => prev.filter(t => t.id !== id));
	}, []);

	const show = useCallback(
		(message, {type = 'info', duration = 5000} = {}) => {
			const id = ++nextId;
			setToasts(prev => [...prev, {id, type, message}]);
			if (duration > 0) {
				timers.current[id] = setTimeout(() => dismiss(id), duration);
			}
			return id;
		},
		[dismiss],
	);

	const error = useCallback(
		(message, opts) => show(message, {type: 'error', ...opts}),
		[show],
	);
	const success = useCallback(
		(message, opts) => show(message, {type: 'success', ...opts}),
		[show],
	);
	const warning = useCallback(
		(message, opts) => show(message, {type: 'warning', ...opts}),
		[show],
	);
	const info = useCallback(
		(message, opts) => show(message, {type: 'info', ...opts}),
		[show],
	);

	return (
		<ToastContext.Provider value={{show, dismiss, error, success, warning, info}}>
			{children}
			{toasts.length > 0 && (
				<ToastContainer>
					{toasts.map(t => (
						<Toast
							key={t.id}
							type={t.type}
							message={t.message}
							onClose={() => dismiss(t.id)}
						/>
					))}
				</ToastContainer>
			)}
		</ToastContext.Provider>
	);
};

export const useToast = () => {
	const ctx = useContext(ToastContext);
	if (!ctx) {
		throw new Error('useToast must be used within a ToastProvider');
	}
	return ctx;
};

export default ToastContext;
